/**
 * The LV assistant panel — CLONED from the Console's Chat.tsx (tc-lab),
 * wired to this server's /LearnerView/api/chat (the phoenix chat routes,
 * cloned for learners). Deliberately a copy: the learner assistant diverges
 * from the console's (per-client prompts, learner tools later) and must be
 * free to without dragging the console along.
 *
 * What survived: the transcript + composer + cap, image AND text-file
 * attachments (staged chip, thumbnails persisted on the message), clear,
 * auto-scroll, send-pulse bounce, the [View …|surface:id] marker buttons,
 * speech input.
 *
 * What was cut (console machinery the learner assistant doesn't have): the
 * surface launcher + My Surfaces + Settings menus (the LV strip owns
 * launching; the shell owns theme), sign-out (shell chrome), surfaceActions
 * command cards, canvas turn args, the Files side-channel, read-aloud TTS
 * (deferred), and every client-declared surface context — the server computes
 * this deployment's surfaces itself.
 *
 * Availability: the initial history load doubles as the probe — a 503 means
 * the assistant isn't configured on this deployment (no provider key / no
 * learner_views store) and onUnavailable() tells the shell to hide the
 * column rather than show a dead panel.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Alert, Avatar, Box, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import { apiFetch } from '../shell/auth'
import MessageBubble, { CAP_AVATAR } from './MessageBubble'
import Composer from './Composer'
import { useSpeechInput } from './useSpeechInput'
import { useScrollToBottom } from './useScrollToBottom'
import { PANEL_BAR_HEIGHT } from './useResizablePanel'
import type { ChatGetResp, ChatMessage, ChatPostResp, ShowInSurfaceArgs, SurfaceOpenTarget } from './types'

// Page context sent to the server (stored as page_url/page_title, not
// matched on). Live origin, because an LV answers on its own per-client host.
const LV_URL = `${window.location.origin}/`
const LV_TITLE = 'Learner View'
const SURFACE_MARKER = /\[View ([^\]|]+)\|surface:([a-z0-9-]+)(\?[^\]]*)?\]/g
const SHOW_MARKER = /\[Show me\|surface:[a-z0-9-]+(\?[^\]\s]*)?\s+(anchor:[^\]\s]+|text:"[^"]+"|selector:[^\]]+)\]/g

function stripMarkers(content: string): string {
  return content.replace(SHOW_MARKER, '').replace(SURFACE_MARKER, '').trim()
}
void stripMarkers // kept for the TTS follow-up (speak() strips markers)

/** Initials from the learner's NAME ("Douglas Wallace" -> DW), and from the
 *  email's local part otherwise ("doug.wallace@" -> DW). */
function initials(nameOrEmail: string): string {
  const base = nameOrEmail.includes('@') ? (nameOrEmail.split('@')[0] ?? '') : nameOrEmail
  const parts = base.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return base.slice(0, 2).toUpperCase()
}

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await apiFetch(path, init)
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as { error?: string } | null
    const err = new Error(body?.error ?? `request failed (${r.status})`) as Error & { status?: number }
    err.status = r.status
    throw err
  }
  return (await r.json()) as T
}

/** One __CHAT_CONTROL__ record from a streaming chat turn (see the server's
 *  routes/chat.ts): 'restart' discards the streamed text, 'done' carries the
 *  authoritative turn payload, 'error' fails it. */
interface ChatStreamControl {
  type: 'restart' | 'done' | 'error'
  error?: string
  [k: string]: unknown
}

/**
 * POST the turn with `stream: true` and read the chunked reply as it forms —
 * the console's apiStreamChat, same rolling LINE buffer: control records
 * only ever START at a line boundary; text chunks render the moment they
 * arrive (partial lines included); a trailing fragment that could be the
 * start of a control token is held until the next chunk disambiguates, so a
 * split control record is never misread as text. onText always receives the
 * FULL accumulated display text — replacing state per call is race-free.
 */
async function apiStreamChat(
  body: unknown,
  handlers: {
    onText: (fullText: string) => void
    onControl: (record: ChatStreamControl) => void
  },
  signal?: AbortSignal,
): Promise<void> {
  const r = await apiFetch('/LearnerView/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  if (!r.ok) {
    const b = (await r.json().catch(() => null)) as { error?: string } | null
    const err = new Error(b?.error ?? `request failed (${r.status})`) as Error & { status?: number }
    err.status = r.status
    throw err
  }
  if (!(r.headers.get('content-type') ?? '').startsWith('text/plain')) {
    // The server behind us predates streaming: it ignored `stream: true`
    // and answered the old buffered JSON. Treat the whole body as the done
    // record — the turn succeeds (unstreamed) instead of dying on a
    // protocol the other end doesn't speak.
    const payload = (await r.json()) as Record<string, unknown>
    handlers.onControl({ type: 'done', ...payload })
    return
  }
  if (!r.body) throw new Error('streaming unsupported by this browser')
  const CONTROL = '__CHAT_CONTROL__:'
  const reader = r.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let text = ''

  const pump = (flush: boolean) => {
    let changed = false
    let nl: number
    while ((nl = buf.indexOf('\n')) !== -1) {
      const line = buf.slice(0, nl)
      buf = buf.slice(nl + 1)
      if (line.startsWith(CONTROL)) {
        try {
          const record = JSON.parse(line.slice(CONTROL.length)) as ChatStreamControl
          if (record.type === 'restart') {
            text = ''
            changed = true
          }
          handlers.onControl(record)
        } catch { /* malformed control record — ignored, the stream continues */ }
      } else {
        text += line + '\n'
        changed = true
      }
    }
    if (flush) {
      if (buf.startsWith(CONTROL)) {
        try { handlers.onControl(JSON.parse(buf.slice(CONTROL.length)) as ChatStreamControl) } catch { /* ignore */ }
      } else if (buf) {
        text += buf
        changed = true
      }
      buf = ''
    } else if (buf && !CONTROL.startsWith(buf) && !buf.startsWith(CONTROL)) {
      // The tail is plain text that can't be the start of a control record —
      // show it NOW; holding whole tokens for their newline would defeat
      // streaming. Two tails stay buffered: a PREFIX of the token (the token
      // itself split across chunks) AND a PARTIAL CONTROL RECORD (token
      // complete, JSON still arriving — buf is then LONGER than the token,
      // so the prefix test alone misses it and the record would be flushed
      // into the prose and never parse: the 'done' that isn't there).
      text += buf
      buf = ''
      changed = true
    }
    if (changed) handlers.onText(text)
  }

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      pump(false)
    }
    buf += decoder.decode()
    pump(true)
  } finally {
    reader.releaseLock()
  }
}

export default function LvChat({
  learnerName,
  learnerEmail,
  onOpenSurface,
  onShowInSurface,
  onUnavailable,
  onClose,
  barActions,
  barLeading,
}: {
  learnerName: string
  learnerEmail: string
  onOpenSurface: (id: string, label?: string, target?: SurfaceOpenTarget) => void
  /** [Show me|…] — degraded to open/focus until the LV bridge can highlight. */
  onShowInSurface: (args: ShowInSurfaceArgs) => void
  /** The assistant isn't configured on this deployment — hide the column. */
  onUnavailable: () => void
  /** Mobile overlay only: an X in the bar that dismisses the assistant. */
  onClose?: () => void
  /** Shell controls that ride in the bar NEXT TO the title (Douglas,
   *  2026-08-28): the night-mode toggle and sign-out, when the desktop split
   *  is up. When the column is hidden the strip carries them instead — the
   *  controls must never vanish with the assistant. */
  barActions?: ReactNode
  /** Leading mark, left of the title: the deployment's brand logo when
   *  brand.logoUrl is set (the client's identity leads top-left). Absent =
   *  the ThinkingCap mark, the unbranded default. */
  barLeading?: ReactNode
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loaded, setLoaded] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  // The in-flight streamed reply's accumulated text (null = no stream, '' =
  // waiting on the first token — the spinner covers that window). SEPARATE
  // from messages so history keeps stable identity during the stream; only
  // the streaming bubble re-renders per chunk.
  const [streamText, setStreamText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sendPulse, setSendPulse] = useState(0)
  // Aborts the in-flight stream (the composer's Stop button).
  const abortRef = useRef<AbortController | null>(null)
  const [pendingFile, setPendingFile] = useState<{ name: string; mediaType: string; data: string; dataUrl: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesRef = useRef<HTMLDivElement | null>(null)

  const speechInput = useSpeechInput((text) => setDraft((prev) => (prev ? `${prev} ${text}` : text)))

  useEffect(() => {
    apiJson<ChatGetResp>('/LearnerView/api/chat')
      .then((r) => setMessages(r.messages))
      .catch((e) => {
        if (typeof e?.status === 'number' && e.status === 503) { onUnavailable(); return }
        setError(e instanceof Error ? e.message : 'failed to load chat')
      })
      .finally(() => setLoaded(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** The upload button's pick: images (png/jpeg/webp/gif — the model sees
   *  these as vision blocks) or text files (txt/csv/md/json/log/xml — the
   *  server inlines their contents into the prompt), ≤ 5 MB, staged until
   *  the next send. */
  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    e.target.value = '' // picking the same file twice must still fire change
    if (!f) return
    // Browsers leave File.type empty for some extensions (.log especially) —
    // fall back to the extension so those files still reach us typed.
    const ext = (f.name.split('.').pop() ?? '').toLowerCase()
    const extTypes: Record<string, string> = {
      txt: 'text/plain', log: 'text/plain', csv: 'text/csv', md: 'text/markdown',
      json: 'application/json', xml: 'text/xml',
    }
    const mediaType = f.type.toLowerCase() || extTypes[ext] || ''
    const isImage = /^image\/(png|jpe?g|webp|gif)$/.test(mediaType)
    const isText = /^(text\/(plain|csv|markdown|xml)|application\/(json|xml))$/.test(mediaType)
    if (!isImage && !isText) {
      setError("That file type isn't supported yet — attach an image (png, jpeg, webp, gif) or a text file (txt, csv, md, json, log, xml).")
      return
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('That file is over 5 MB — chat attachments are capped there.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      const m = /^data:([^;]*);base64,(.*)$/.exec(dataUrl)
      if (!m) { setError('Could not read that file.'); return }
      setPendingFile({ name: f.name, mediaType, data: m[2], dataUrl })
    }
    reader.onerror = () => setError('Could not read that file.')
    reader.readAsDataURL(f)
  }

  // Follow the transcript while pinned to the bottom; a learner who scrolled
  // up into history is never yanked back down by an arriving token. Instant
  // pre-paint jumps, not 'smooth' — per-chunk smooth animations queue and
  // stutter.
  useScrollToBottom(messagesRef, [messages, sending, streamText])

  async function send() {
    const staged = pendingFile
    // A staged upload with no typed text still sends — the file IS the
    // message, and its name is what the transcript says.
    const text = draft.trim() || (staged ? `Shared a file: ${staged.name}` : '')
    if (!text || sending) return
    setDraft('')
    setSending(true)
    setSendPulse((p) => p + 1)
    setError(null)
    // The optimistic user bubble's id — negative, so it can never collide
    // with a DB id; the done payload's authoritative messages replace it.
    let tempId = 0
    try {
      const body: Record<string, unknown> = { url: LV_URL, title: LV_TITLE, kind: 'typed', message: text }
      if (staged) body.attachments = [{ mediaType: staged.mediaType, data: staged.data, filename: staged.name }]
      // ── Streaming turn: the question appears NOW, the reply forms
      // token-by-token in a separate bubble, and the server's done record
      // lands the authoritative messages to reconcile to. ────────────────
      body.stream = true
      tempId = -Date.now()
      setMessages((prev) => [...prev, {
        id: tempId,
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
        ...(staged
          ? { metadata: { attachments: [{ mediaType: staged.mediaType, data: staged.data, filename: staged.name }] } }
          : {}),
      }])
      setStreamText('')
      const ac = new AbortController()
      abortRef.current = ac
      let done: ChatPostResp | null = null
      await apiStreamChat(body, {
        onText: (t) => setStreamText(t),
        onControl: (record) => {
          if (record.type === 'done') done = record as unknown as ChatPostResp
          else if (record.type === 'error') throw new Error(record.error || 'chat failed')
          // 'restart' needs nothing here — apiStreamChat already cleared the
          // accumulated text (a failover discarded the dead attempt's prose).
        },
      }, ac.signal)
      if (!done) throw new Error('the reply ended before it completed')
      const r: ChatPostResp = done
      setMessages((prev) => [...prev.filter((m) => m.id !== tempId), ...r.newMessages])
      if (staged) setPendingFile(null)
      // Bang again when the reply lands — one bounce on send, one on receive.
      setSendPulse((p) => p + 1)
    } catch (e) {
      // The optimistic bubble comes back off (the turn "didn't happen", the
      // draft is restored) — visual parity with the old buffered failure.
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Stop is a choice, not a failure — no error banner.
      } else {
        setError(e instanceof Error ? e.message : 'failed to send')
      }
      setDraft(text)
    } finally {
      setSending(false)
      setStreamText(null)
      abortRef.current = null
    }
  }

  /** The composer's Stop button: aborts the fetch; the server's req-close
   *  hook aborts the upstream provider stream in step. */
  function stopStreaming() {
    abortRef.current?.abort()
  }

  async function clearChat() {
    try {
      await apiJson('/LearnerView/api/chat/clear', { method: 'POST' })
      setMessages([])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to clear chat')
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden', position: 'relative' }}>
      {/* The assistant's own bar, pinned to the SAME height as the surface
          strip so the two columns' divider lines read as one rule. Shell
          controls (night mode, sign out) sit immediately right of the leading
          mark; Clear lives down with the composer actions, not here. The
          leading mark is the deployment's brand logo when one is set
          (barLeading from the shell); the ThinkingCap mark otherwise. No
          title text (Douglas 2026-09-20): the mark carries the bar. */}
      <Stack
        direction="row" alignItems="center" spacing={1.25}
        sx={{ px: 2, height: PANEL_BAR_HEIGHT, boxSizing: 'border-box', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        {barLeading ?? (
          <Box
            component="img" src="/brand/thinking-cap-logo.png" alt=""
            sx={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
          />
        )}
        {barActions}
        <Box sx={{ flex: 1 }} />
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="Close the assistant">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Stack>

      {/* minHeight:0 lets this flex child actually shrink and own its scroll —
          without it the auto-scroll scrolls an ancestor and the header gets
          pushed off the top. */}
      <Box ref={messagesRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', px: 3, py: 2 }}>
        {!loaded ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
        ) : (
          <Stack spacing={2} sx={{ maxWidth: 760, mx: 'auto' }}>
            {messages.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Ask me anything to get started.
              </Typography>
            )}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                role={m.role}
                content={m.content}
                onOpenSurface={onOpenSurface}
                onShowInSurface={onShowInSurface}
                userAvatar={
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'action.selected', color: 'text.primary' }}>
                    {initials(learnerName || learnerEmail)}
                  </Avatar>
                }
              >
                {m.role === 'user' && m.metadata?.attachments?.map((a, i) => (
                  // The upload's thumbnail, re-rendered from the message's own
                  // stored metadata — it survives reloads because the server
                  // keeps the base64 on the row. Text files get a file chip.
                  a.mediaType.startsWith('image/')
                    ? (
                      <Box
                        key={i}
                        component="img"
                        src={`data:${a.mediaType};base64,${a.data}`}
                        alt={a.filename ?? 'attachment'}
                        sx={{ mt: 1, maxWidth: 240, maxHeight: 180, borderRadius: 2, display: 'block' }}
                      />
                    )
                    : (
                      <Stack
                        key={i}
                        direction="row" alignItems="center" spacing={0.75}
                        sx={{ mt: 1, maxWidth: 240, color: 'text.secondary' }}
                      >
                        <InsertDriveFileIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                        <Typography variant="caption" noWrap>{a.filename ?? 'attached file'}</Typography>
                      </Stack>
                    )
                ))}
              </MessageBubble>
            ))}
            {sending && !streamText && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-start' }}>
                {CAP_AVATAR}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                  <CircularProgress size={14} />
                </Paper>
              </Stack>
            )}
            {streamText ? (
              // The reply forming live. Only THIS bubble re-parses markdown
              // per chunk; settled bubbles are memoized (MessageContent) and
              // skip the render entirely.
              <MessageBubble
                role="assistant"
                content={streamText}
                onOpenSurface={onOpenSurface}
                onShowInSurface={onShowInSurface}
              />
            ) : null}
          </Stack>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 3, mb: 1 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* The staged upload: a thumbnail of what's about to ride along, with
          an X to change your mind. It sends with the next turn (text optional
          — the file can BE the message). */}
      {pendingFile && (
        <Box sx={{ px: 3, pb: 0.5, flexShrink: 0 }}>
          <Stack
            direction="row" alignItems="center" spacing={1}
            sx={{ maxWidth: 760, mx: 'auto', p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            {pendingFile.mediaType.startsWith('image/')
              ? (
                <Box
                  component="img" src={pendingFile.dataUrl} alt=""
                  sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                />
              )
              : <InsertDriveFileIcon sx={{ fontSize: 28, color: 'text.secondary', flexShrink: 0 }} />}
            <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>{pendingFile.name}</Typography>
            <IconButton size="small" onClick={() => setPendingFile(null)} aria-label="remove attachment">
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        </Box>
      )}

      <Composer
        value={draft}
        onChange={setDraft}
        onSend={() => void send()}
        onStop={stopStreaming}
        sending={sending}
        pulseKey={sendPulse}
        autoFocus
        trailingActions={<>
          <Tooltip title="Clear the conversation">
            <IconButton size="large" onClick={() => void clearChat()} aria-label="clear the conversation">
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
          {speechInput.supported && (
            <Tooltip title={speechInput.listening ? 'Stop listening' : 'Speak your question'}>
              <IconButton
                size="large"
                color={speechInput.listening ? 'error' : 'default'}
                onClick={speechInput.toggle}
                sx={speechInput.listening ? { animation: 'pulse 1.4s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } } } : undefined}
              >
                {speechInput.listening ? <MicIcon /> : <MicOffIcon sx={{ opacity: 0.5 }} />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Attach a file to your message">
            <IconButton size="large" onClick={() => fileInputRef.current?.click()} aria-label="attach a file">
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
          <input
            ref={fileInputRef} type="file" hidden
            accept="image/png,image/jpeg,image/webp,image/gif,text/plain,text/csv,text/markdown,application/json,text/xml,.txt,.csv,.md,.json,.log,.xml"
            onChange={onFilePicked}
          />
        </>}
      />
    </Box>
  )
}
